

import { Subscription } from 'rxjs';
import { RefObject, useEffect, useState } from 'react';

import { CoordinatePoint2, nearestSixteenth } from '@rta/rta-blueprint-library';

import { calculateSvgPpi } from './calculate-svg-ppi';
import { InteractionData } from './interaction-data.type';
import { useInteractionService } from './interaction-context';

export type UseInteractionOptions = {
	// stop tracking, preventing refreshes when false
	active?: boolean;
	// hook will only report position when over the provided SVG
	// scopeTracking: boolean;
};

export type UseInteraction = [
	InteractionData,
	{
		setActive: (boolean) => void;
		setSvg: (svgElement: SVGElement | SVGSVGElement | null) => void;
	}
];

export const useInteraction = (svgProp?: RefObject<SVGElement | SVGSVGElement>, options: UseInteractionOptions = {}): UseInteraction => {
	const interactionService = useInteractionService();
	const [isActive, setIsActive] = useState<boolean>(options.active ?? true);
	const [svg, setSvg] = useState<SVGSVGElement | null>(null);
	const [data, setData] = useState<InteractionData>({
		selected: [],
		keys: [],
		target: null,
		click: { x: 0, y: 0, target: null },
		dblClick: { x: 0, y: 0, target: null },
		pointer: { x: 0, y: 0 },
		drag: { x: 0, y: 0, dx: 0, dy: 0, kx: 0, ky: 0, distance: 0 },
		raw: { x: 0, y: 0 },
		velocity: { x: 0, y: 0 },
		ppi: { x: 0, y: 0 },
		swipe: null,
		isDragging: false,
		isActive: options.active ?? true
	});

	useEffect(() => {
		if (svgProp && svgProp.current) {
			// console.log('svgProp.current instanceof SVGSVGElement', svgProp.current, svgProp.current instanceof SVGSVGElement, svgProp.current instanceof SVGElement);
			if (svgProp.current instanceof SVGSVGElement) {
				// console.log('svgProp.current instanceof SVGSVGElement', svgProp.current);
				setSvg(svgProp.current);
			} else {
				const tempSvg = svgProp.current.ownerSVGElement as SVGSVGElement | null;
				// console.log('svgProp.current instanceof SVGElement', tempSvg);
				setSvg(tempSvg);
			}
		}
	}, [svgProp?.current]);

	useEffect(() => {
		data.isActive = isActive;
	}, [isActive]);

	const handleSetSvg = (svgElement: SVGElement | SVGSVGElement | null) => {
		if (svgElement) {
			if (svgElement instanceof SVGSVGElement) {
				setSvg(svgElement);
			} else {
				const tempSvg = svgElement.ownerSVGElement as SVGSVGElement | null;
				setSvg(tempSvg);
			}
		} else {
			setSvg(null);
		}
	};

	const convertToSvgPoint = (screenPt: CoordinatePoint2<number>): CoordinatePoint2<number> => {
		const ctm = svg?.getScreenCTM();
		const pt = svg?.createSVGPoint?.();
		if (ctm && pt) {
			pt.x = screenPt.x;
			pt.y = screenPt.y;
			const npt = pt.matrixTransform(ctm.inverse());
			return {
				x: nearestSixteenth(npt.x),
				y: nearestSixteenth(npt.y)
			};
		}
		return null;
	};

	useEffect(() => {
		let sub: Subscription | null = null;
		// console.log('useMousePosition', svg, svgProp);
		if (data.isActive) {
			sub = interactionService.stream.subscribe((msData) => {
				if (svg) {
					// if (pt && svg?.getScreenCTM) {
					const newData = { ...msData, drag: { ...msData.drag }};
					// const ctm = svg.getScreenCTM();

					const ppi = calculateSvgPpi(svg);
					if (ppi) newData.ppi = ppi;

					newData.pointer = convertToSvgPoint(msData.raw);

					// drag {x,y} is set on click before dragging flag is set
					if (newData.isDragging) {
						const dragStart = convertToSvgPoint({ x: msData.drag.x, y: msData.drag.y });
						newData.drag.x = dragStart.x + (msData.drag.kx * (svg ? 0.0625 : 1));
						newData.drag.y = dragStart.y + (msData.drag.ky * (svg ? 0.0625 : 1));
					}

					// update drag delta and distance when dragging
					if (newData.isDragging) {
						const dragDelta = {
							x: newData.pointer.x - newData.drag.x,
							y: newData.pointer.y - newData.drag.y
						};
						newData.drag = {
							...newData.drag,
							dx: dragDelta.x,
							dy: dragDelta.y,
							distance: Math.sqrt(dragDelta.x ** 2 + dragDelta.y ** 2)
						};
					}

					const clickPt = msData.click;
					if (clickPt.x + clickPt.y > 0) {
						newData.click = {
							...msData.click,
							...convertToSvgPoint(clickPt)
						};
					}

					const dblClickPt = msData.dblClick;
					if (dblClickPt.x + dblClickPt.y > 0) {
						newData.dblClick = {
							...msData.dblClick,
							...convertToSvgPoint(dblClickPt)
						};
					}

					setData(newData);
				} else {
					setData(msData);
				}
				// return;
			});
		}

		return () => sub?.unsubscribe();
	}, [svg, isActive]);

	return [data, { setActive: setIsActive, setSvg: handleSetSvg }];
};
